import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Bike,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Clock3,
  FileText,
  Headphones,
  Menu,
  WalletCards,
  X,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import AppShell from "app/AppShell";
import courierHeroImage from "assets/images/_V6A0037.jpg";
import benefitsCourierImage from "assets/images/_V6A0865.jpg";
import requirementsCourierImage from "assets/images/_V6A0652.jpg";
import FaqAnswer from "features/welcome/components/FaqAnswer";
import Footer from "shared/components/Footer";
import LanguageSwitcher from "shared/components/LanguageSwitcher";
import { useLanguage } from "shared/context/LanguageContext";
import { useWelcomeContent } from "features/courier-onboarding/components/welcome/useWelcomeContent";
import {
  normalizeLang,
  pick,
} from "features/courier-onboarding/components/welcome/welcomeContent";

const HAAT_LOGO_URL =
  "https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg";

const BENEFIT_ICONS: LucideIcon[] = [Clock3, WalletCards, Headphones];
const REQUIREMENT_KEYS = ["documents", "vehicle", "phone"] as const;
const REGISTRATION_ICONS: LucideIcon[] = [
  FileText,
  ClipboardList,
  CheckCircle2,
  Bike,
];
const REGISTRATION_KEYS = ["documents", "details", "review", "start"] as const;
const FAQ_KEYS = ["q1", "q2", "q3", "q4", "q5", "q6"] as const;
const NAV_ITEMS = [
  { id: "why-haat", labelKey: "whyHaat" },
  { id: "requirements", labelKey: "requirements" },
  { id: "registration", labelKey: "registration" },
  { id: "faq", labelKey: "faq" },
] as const;

const SectionHeading: React.FC<{
  title: string;
  subtitle?: string;
  eyebrow?: string;
  light?: boolean;
}> = ({ title, subtitle, eyebrow, light = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.35 }}
    transition={{ duration: 0.5 }}
    className="mx-auto mb-12 max-w-2xl text-center sm:mb-16"
  >
    {eyebrow ? (
      <p className="mb-4 text-sm font-bold uppercase tracking-[0.22em] text-[#bf0035] rtl:normal-case rtl:tracking-normal">
        {eyebrow}
      </p>
    ) : null}
    <h2
      className={`text-[clamp(1.875rem,5vw,3rem)] font-extrabold leading-tight ${light ? "text-white" : "text-gray-950"}`}
    >
      {title}
    </h2>
    {subtitle ? (
      <p
        className={`mt-4 text-base leading-7 sm:text-lg ${light ? "text-white/75" : "text-gray-600"}`}
      >
        {subtitle}
      </p>
    ) : null}
  </motion.div>
);

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { dir } = useLanguage();
  const { content } = useWelcomeContent();
  const lang = normalizeLang(i18n.language);
  const [openFaq, setOpenFaq] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navSentinelRef = useRef<HTMLDivElement>(null);
  const ForwardIcon = dir === "rtl" ? ArrowLeft : ArrowRight;
  const cmsFaqs = content?.faq ?? content?.faqs ?? [];
  const faqs =
    cmsFaqs.length > 0
      ? [...cmsFaqs]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((faq) => ({
            key: String(faq.id),
            question: pick(faq, "question", lang),
            answer: pick(faq, "answer", lang),
          }))
      : FAQ_KEYS.map((key) => ({
          key,
          question: String(t(`landingPage.faq.items.${key}.question`)),
          answer: String(t(`landingPage.faq.items.${key}.answer`)),
        }));

  const startRegistration = useCallback(
    () => navigate("/onboarding"),
    [navigate],
  );

  useEffect(() => {
    const sentinel = navSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsScrolled(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const scrollToSection = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setMobileMenuOpen(false);
  }, []);

  const renderCta = (extraClasses = "") => (
    <button
      type="button"
      onClick={startRegistration}
      className={`group inline-flex min-h-14 items-center justify-center gap-2 rounded-full bg-[#bf0035] px-8 text-base font-bold text-white shadow-xl shadow-black/20 transition hover:bg-[#a6002e] focus:outline-none focus:ring-4 focus:ring-[#bf0035]/30 ${extraClasses}`}
    >
      {t("landing.cards.new.cta")}
      <ForwardIcon className="h-5 w-5 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
    </button>
  );

  return (
    <AppShell fullBleed hideHeader>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45 }}
        className="pt-4 sm:pt-6 lg:pt-8"
      >
        <div
          ref={navSentinelRef}
          className="h-px bg-white"
          aria-hidden="true"
        />
        <header
          className={`sticky top-0 z-50 transition-colors duration-300 ${
            isScrolled
              ? "border-b border-gray-200/70 bg-white/85 shadow-sm backdrop-blur-xl"
              : "border-b border-transparent bg-transparent"
          }`}
        >
          <div className="relative mx-4 sm:mx-6 lg:mx-8">
            <nav
              className={`relative grid h-16 w-full grid-cols-[auto_1fr_auto] items-center gap-2 transition-[top] duration-300 sm:gap-4 ${
                isScrolled ? "top-0" : "top-4"
              }`}
            >
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                aria-label={String(t("appName"))}
                className="shrink-0"
              >
                <img
                  src={HAAT_LOGO_URL}
                  alt={String(t("appName"))}
                  className="h-8 w-auto sm:h-10"
                  loading="eager"
                  decoding="async"
                />
              </button>

              <div className="hidden items-center justify-center gap-5 lg:flex xl:gap-7">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      scrollToSection(item.id);
                    }}
                    className={`whitespace-nowrap text-sm font-bold transition-colors ${
                      isScrolled
                        ? "text-gray-700 hover:text-[#bf0035]"
                        : "text-white/90 drop-shadow-sm hover:text-white"
                    }`}
                  >
                    {t(`landingPage.nav.${item.labelKey}`)}
                  </a>
                ))}
              </div>

              <div className="flex items-center justify-end gap-1.5 sm:gap-3">
                <LanguageSwitcher
                  variant="header"
                  compactOnMobile
                  landingAccent
                />
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className={`inline-flex min-h-10 items-center rounded-full px-3 text-xs font-bold transition sm:px-4 sm:text-sm ${
                    isScrolled
                      ? "bg-[#bf0035] text-white hover:bg-[#a6002e]"
                      : "border border-white/30 bg-black/15 text-white backdrop-blur hover:bg-black/25"
                  }`}
                >
                  {t("landingPage.nav.login")}
                </button>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen((open) => !open)}
                  aria-expanded={mobileMenuOpen}
                  aria-label={String(t("landingPage.nav.menuLabel"))}
                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition lg:hidden ${
                    isScrolled
                      ? "border-gray-200 bg-white text-gray-900 hover:bg-gray-50"
                      : "border-white/25 bg-black/15 text-white backdrop-blur hover:bg-black/25"
                  }`}
                >
                  {mobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </button>
              </div>
            </nav>

            <AnimatePresence>
              {mobileMenuOpen ? (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 right-0 top-full px-2 pt-2 sm:px-4"
                >
                  <div className="mx-auto max-w-7xl overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-xl">
                    {NAV_ITEMS.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        onClick={(event) => {
                          event.preventDefault();
                          scrollToSection(item.id);
                        }}
                        className="block rounded-xl px-4 py-3 text-start text-sm font-bold text-gray-800 transition hover:bg-[#bf0035]/5 hover:text-[#bf0035]"
                      >
                        {t(`landingPage.nav.${item.labelKey}`)}
                      </a>
                    ))}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </header>

        <section className="-mt-16 h-[calc(100svh-1rem)] min-h-[620px] w-full bg-white px-4 pb-4 sm:h-[calc(100svh-1.5rem)] sm:min-h-[700px] sm:px-6 sm:pb-6 lg:h-[calc(100svh-2rem)] lg:px-8 lg:pb-8">
          <div className="relative isolate h-full w-full overflow-hidden rounded-[2rem] bg-gray-950">
            <img
              src={courierHeroImage}
              alt={String(t("landingPage.hero.imageAlt"))}
              className="absolute inset-0 h-full w-full object-cover object-center"
              loading="eager"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10 rtl:bg-gradient-to-l" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10" />

            <div className="relative mx-auto flex h-full max-w-7xl items-center px-5 pb-16 pt-24 sm:px-8 lg:px-12">
              <div className="max-w-3xl text-start text-white">
                <motion.h1
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12, duration: 0.6 }}
                  className="max-w-2xl text-[clamp(2.25rem,9vw,4.5rem)] font-black leading-[1.02] tracking-tight sm:leading-[0.98]"
                >
                  {t("landingPage.hero.title")}
                  <span className="block text-[#ffcc00]">
                    {t("landingPage.hero.titleAccent")}
                  </span>
                </motion.h1>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.24, duration: 0.6 }}
                  className="mt-9 flex flex-col gap-3 sm:flex-row"
                >
                  {renderCta("w-full sm:w-auto")}
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="why-haat"
          className="scroll-mt-20 bg-white px-5 py-16 sm:px-8 sm:py-20"
        >
          <div className="mx-auto grid max-w-7xl items-stretch gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 40 : -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55 }}
              className="flex flex-col text-start"
            >
              <p className="mb-4 text-sm font-bold uppercase tracking-[0.22em] text-[#bf0035] rtl:normal-case rtl:tracking-normal">
                {t("landingPage.benefits.eyebrow")}
              </p>
              <h2 className="max-w-xl text-[clamp(2rem,7vw,3.75rem)] font-black leading-[1.05] text-gray-950">
                {t("landingPage.benefits.title")}
              </h2>
              <div className="mt-9 min-h-[300px] flex-1 overflow-hidden rounded-3xl sm:min-h-[340px]">
                <img
                  src={benefitsCourierImage}
                  alt={String(t("landingPage.hero.imageAlt"))}
                  className="h-full w-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>

            <div className="flex flex-col justify-center gap-2 lg:ps-2">
              {BENEFIT_ICONS.map((Icon, index) => (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ delay: index * 0.08 }}
                  className="flex items-start gap-4 border-b border-gray-200 py-5 last:border-b-0"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#bf0035]/10 text-[#bf0035]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 self-center">
                    <h3 className="text-lg font-bold text-gray-950">
                      {t(`landingPage.benefits.items.${index + 1}.title`)}
                    </h3>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="requirements"
          className="scroll-mt-20 bg-[#bf0035] px-5 py-16 sm:px-8 sm:py-20"
        >
          <div className="mx-auto grid max-w-7xl items-stretch gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 40 : -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              className="text-start text-white"
            >
              <h2 className="text-[clamp(2rem,7vw,3rem)] font-black leading-tight">
                {t("landingPage.documents.title")}
              </h2>
              <p className="mt-4 max-w-lg text-lg leading-7 text-white/80">
                {t("landingPage.documents.subtitle")}
              </p>

              <div className="mt-6 border-t border-white/25">
                {REQUIREMENT_KEYS.map((key, index) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06 }}
                    className="flex items-start gap-3 border-b border-white/25 py-3.5 text-white"
                  >
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ffcc00] text-[#bf0035]">
                      <Check className="h-4 w-4" strokeWidth={3} />
                    </span>
                    <div className="self-center">
                      <h3 className="text-base font-bold">
                        {t(`landingPage.documents.items.${key}.title`)}
                      </h3>
                      <p className="mt-0.5 leading-5 text-white/75">
                        {t(`landingPage.documents.items.${key}.description`)}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              className="relative min-h-[480px] overflow-hidden rounded-[2rem] lg:min-h-0"
            >
              <img
                src={requirementsCourierImage}
                alt={String(t("landingPage.hero.imageAlt"))}
                className="absolute inset-0 h-full w-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </motion.div>
          </div>
        </section>

        <section
          id="registration"
          className="scroll-mt-20 bg-white px-5 py-16 sm:px-8 sm:py-20"
        >
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              title={String(t("landingPage.registration.title"))}
            />
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {REGISTRATION_ICONS.map((Icon, index) => (
                <motion.article
                  key={REGISTRATION_KEYS[index]}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ delay: index * 0.08 }}
                  className="group relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <span className="absolute end-5 top-3 text-6xl font-black text-gray-100">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-[#bf0035]/10 text-[#bf0035]">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="relative mt-8 text-xl font-bold text-gray-950">
                    {t(
                      `landingPage.registration.steps.${REGISTRATION_KEYS[index]}.title`,
                    )}
                  </h3>
                  <p className="relative mt-3 leading-7 text-gray-600">
                    {t(
                      `landingPage.registration.steps.${REGISTRATION_KEYS[index]}.description`,
                    )}
                  </p>
                </motion.article>
              ))}
            </div>
            <div className="mt-12 flex justify-center">
              {renderCta("w-full sm:w-auto")}
            </div>
          </div>
        </section>

        <section
          id="faq"
          className="scroll-mt-20 bg-white px-5 py-16 sm:px-8 sm:py-20"
        >
          <div className="mx-auto max-w-3xl">
            <SectionHeading
              title={String(t("landingPage.faq.title"))}
              subtitle={String(t("landingPage.faq.subtitle"))}
            />
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index;
                const panelId = `landing-faq-${faq.key}`;
                return (
                  <div key={faq.key}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() =>
                        setOpenFaq((current) =>
                          current === index ? -1 : index,
                        )
                      }
                      className="flex w-full items-center justify-between gap-6 py-6 text-start text-lg font-bold text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035]"
                    >
                      <span>{faq.question}</span>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-[#bf0035] transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen ? (
                        <motion.div
                          id={panelId}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.24 }}
                          className="overflow-hidden"
                        >
                          <FaqAnswer answer={faq.answer} />
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </motion.div>

      <Footer dark />
    </AppShell>
  );
};

export default WelcomePage;
